/* eslint-disable class-methods-use-this */
/**
 * Microsoft Clarity Helper
 * Wraps Clarity for UX insights and session recordings
 */
export class ClarityHelper {
  constructor({ projectId }) {
    this.projectId = projectId;
    this.initialized = false;
  }

  init() {
    if (this.initialized || !window.clarity) return;
    this.initialized = true;
  }

  /**
   * Identify user in Clarity
   * Helps correlate sessions with user accounts
   * @param {Object} user - User object
   */
  identify(user) {
    if (!window.clarity) return;

    // Clarity supports custom user IDs
    window.clarity('identify', `user-${user.id}`, user.email, user.name);

    // Set custom tags for filtering
    this.setTag('user_id', user.id.toString());
    this.setTag('account_id', user.account_id?.toString());
  }

  /**
   * Track a custom event in Clarity
   * @param {string} eventName - Event name
   * @param {Object} properties - Event properties (converted to tags)
   */
  track(eventName, properties = {}) {
    if (!window.clarity) return;

    // Log the event
    window.clarity('event', eventName);

    // Set tags for the event context
    Object.entries(properties).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        this.setTag(`${eventName}_${key}`, value.toString());
      }
    });
  }

  /**
   * Track page view
   * @param {string} pageName - Page name
   * @param {Object} properties - Page properties
   */
  page(pageName, properties = {}) {
    if (!window.clarity) return;

    this.setTag('page_name', pageName);
    this.setTag('page_path', window.location.pathname);

    // Set any additional properties as tags
    Object.entries(properties).forEach(([key, value]) => {
      if (typeof value === 'string' || typeof value === 'number') {
        this.setTag(`page_${key}`, value.toString());
      }
    });
  }

  /**
   * Set a custom tag in Clarity
   * Tags are key-value pairs for filtering recordings
   * @param {string} key - Tag key
   * @param {string} value - Tag value
   */
  setTag(key, value) {
    if (!window.clarity || !value) return;

    window.clarity('set', key, value);
  }

  /**
   * Mark a conversion point
   * @param {string} conversionName - Name of the conversion
   */
  markConversion(conversionName) {
    if (!window.clarity) return;

    window.clarity('event', `conversion_${conversionName}`);
    this.setTag('conversion', conversionName);
  }

  /**
   * Mark a frustration point (UX issue)
   * @param {string} issueType - Type of frustration
   */
  markFrustration(issueType) {
    if (!window.clarity) return;

    window.clarity('event', `frustration_${issueType}`);
  }

  /**
   * Update Clarity consent (no-op - Clarity doesn't have explicit consent API)
   */
  updateConsent() {
    // Clarity doesn't have a direct consent API
    // It respects cookie settings automatically
  }

  /**
   * Reset Clarity (for logout)
   */
  reset() {
    // Clarity doesn't have a reset method
    // User identification will change with the new session
  }
}
